import React, { useContext, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FiArrowLeft, FiLock, FiShield } from "react-icons/fi";
import { CartContext } from "./CartContext";
import productData from "../Datas/productData";
import "../css/checkout.css";
import siteConfig, { formatPrice } from "../config/siteConfig";

const emptyForm = {
  email: "",
  firstName: "",
  lastName: "",
  company: "",
  address: "",
  apartment: "",
  city: "",
  postalCode: "",
  country: "Belgium",
  cardNumber: "",
  expiry: "",
  cvc: "",
  cardName: "",
};

const CheckOut = () => {
  const navigate = useNavigate();
  const { cartItems, clearCart } = useContext(CartContext);
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [shippingMethod, setShippingMethod] = useState("standard");
  const [billingSame, setBillingSame] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const lines = useMemo(
    () =>
      cartItems
        .map((item) => ({
          ...item,
          product: productData.find((product) => product.id === item.id),
        }))
        .filter((item) => item.product),
    [cartItems]
  );

  const subtotal = lines.reduce(
    (total, item) => total + item.product.price * item.quantity,
    0
  );
  const standardCost =
    subtotal >= siteConfig.freeShippingThreshold ? 0 : siteConfig.standardShipping;
  const shipping = shippingMethod === "express" ? 15 : standardCost;
  const total = subtotal + shipping;

  const updateField = (event) => {
    const { name, value } = event.target;
    let nextValue = value;

    if (name === "cardNumber") {
      nextValue = value.replace(/\D/g, "").slice(0, 16).replace(/(.{4})/g, "$1 ").trim();
    }
    if (name === "expiry") {
      const digits = value.replace(/\D/g, "").slice(0, 4);
      nextValue = digits.length > 2 ? `${digits.slice(0, 2)} / ${digits.slice(2)}` : digits;
    }
    if (name === "cvc") nextValue = value.replace(/\D/g, "").slice(0, 4);

    setForm((current) => ({ ...current, [name]: nextValue }));
    if (errors[name]) setErrors((current) => ({ ...current, [name]: "" }));
  };

  const validate = () => {
    const nextErrors = {};
    const required = [
      "email",
      "firstName",
      "lastName",
      "address",
      "city",
      "postalCode",
      "cardNumber",
      "expiry",
      "cvc",
      "cardName",
    ];

    required.forEach((field) => {
      if (!form[field].trim()) nextErrors[field] = "Dit veld is verplicht.";
    });
    if (form.email && !/^\S+@\S+\.\S+$/.test(form.email)) {
      nextErrors.email = "Vul een geldig e-mailadres in.";
    }
    if (form.cardNumber.replace(/\s/g, "").length !== 16) {
      nextErrors.cardNumber = "Vul een geldig kaartnummer in.";
    }
    if (form.expiry.replace(/\D/g, "").length !== 4) {
      nextErrors.expiry = "Gebruik MM / JJ.";
    }
    if (form.cvc.length < 3) nextErrors.cvc = "Vul een geldige CVC in.";

    return nextErrors;
  };

  const submitOrder = (event) => {
    event.preventDefault();
    const nextErrors = validate();
    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      const firstInvalid = Object.keys(nextErrors)[0];
      document.getElementById(firstInvalid)?.focus();
      return;
    }

    setSubmitting(true);
    window.setTimeout(() => {
      const orderNumber = `DOM-${Date.now().toString().slice(-8)}`;
      clearCart();
      navigate("/paysuccess", {
        replace: true,
        state: { orderNumber, email: form.email, total },
      });
      window.scrollTo({ top: 0 });
    }, 850);
  };

  if (lines.length === 0) {
    return (
      <main className="checkout-empty page-shell">
        <p className="eyebrow">Checkout</p>
        <h1>Je shopping bag is leeg.</h1>
        <Link className="solid-button" to="/shop">Terug naar de collectie</Link>
      </main>
    );
  }

  const fieldClass = (name) => `checkout-field ${errors[name] ? "has-error" : ""}`;

  return (
    <main className="checkout app-page-enter">
      <div className="checkout__heading">
        <Link to="/cartpage"><FiArrowLeft aria-hidden="true" /> Shopping bag</Link>
        <div className="checkout__steps" aria-label="Checkout progress">
          <span className="is-active">01 Information</span>
          <span className="is-active">02 Delivery</span>
          <span className="is-active">03 Payment</span>
        </div>
        <span><FiLock aria-hidden="true" /> Secure checkout</span>
      </div>

      <div className="checkout__layout">
        <form className="checkout-form" onSubmit={submitOrder} noValidate>
          <section>
            <div className="checkout-form__section-title">
              <span>01</span><h1>Contact</h1>
            </div>
            <div className={fieldClass("email")}>
              <label htmlFor="email">E-mail</label>
              <input id="email" name="email" type="email" value={form.email} onChange={updateField} autoComplete="email" />
              {errors.email && <p>{errors.email}</p>}
            </div>
            <label className="checkout-check">
              <input type="checkbox" />
              Houd me op de hoogte van circular news en nieuwe collecties.
            </label>
          </section>

          <section>
            <div className="checkout-form__section-title">
              <span>02</span><h2>Delivery</h2>
            </div>
            <div className="checkout-form__grid">
              <div className={fieldClass("firstName")}>
                <label htmlFor="firstName">Voornaam</label>
                <input id="firstName" name="firstName" value={form.firstName} onChange={updateField} autoComplete="given-name" />
                {errors.firstName && <p>{errors.firstName}</p>}
              </div>
              <div className={fieldClass("lastName")}>
                <label htmlFor="lastName">Achternaam</label>
                <input id="lastName" name="lastName" value={form.lastName} onChange={updateField} autoComplete="family-name" />
                {errors.lastName && <p>{errors.lastName}</p>}
              </div>
            </div>
            <div className={fieldClass("company")}>
              <label htmlFor="company">Bedrijf <span>optional</span></label>
              <input id="company" name="company" value={form.company} onChange={updateField} autoComplete="organization" />
            </div>
            <div className={fieldClass("address")}>
              <label htmlFor="address">Adres</label>
              <input id="address" name="address" value={form.address} onChange={updateField} autoComplete="street-address" />
              {errors.address && <p>{errors.address}</p>}
            </div>
            <div className={fieldClass("apartment")}>
              <label htmlFor="apartment">Appartement, suite <span>optional</span></label>
              <input id="apartment" name="apartment" value={form.apartment} onChange={updateField} />
            </div>
            <div className="checkout-form__grid checkout-form__grid--address">
              <div className={fieldClass("city")}>
                <label htmlFor="city">Plaats</label>
                <input id="city" name="city" value={form.city} onChange={updateField} autoComplete="address-level2" />
                {errors.city && <p>{errors.city}</p>}
              </div>
              <div className={fieldClass("postalCode")}>
                <label htmlFor="postalCode">Postcode</label>
                <input id="postalCode" name="postalCode" value={form.postalCode} onChange={updateField} autoComplete="postal-code" />
                {errors.postalCode && <p>{errors.postalCode}</p>}
              </div>
              <div className={fieldClass("country")}>
                <label htmlFor="country">Land</label>
                <select id="country" name="country" value={form.country} onChange={updateField} autoComplete="country-name">
                  {[
                    "Belgium",
                    "Netherlands",
                    "Luxembourg",
                    "France",
                    "Germany",
                    "Austria",
                    "Denmark",
                  ].map((country) => <option key={country}>{country}</option>)}
                </select>
              </div>
            </div>

            <fieldset className="checkout-shipping">
              <legend>Shipping method</legend>
              <label className={shippingMethod === "standard" ? "is-selected" : ""}>
                <input type="radio" name="shipping" value="standard" checked={shippingMethod === "standard"} onChange={() => setShippingMethod("standard")} />
                <span><strong>Standard delivery</strong><small>2–4 werkdagen, track & trace</small></span>
                <b>{standardCost === 0 ? "Free" : formatPrice(standardCost)}</b>
              </label>
              <label className={shippingMethod === "express" ? "is-selected" : ""}>
                <input type="radio" name="shipping" value="express" checked={shippingMethod === "express"} onChange={() => setShippingMethod("express")} />
                <span><strong>Express delivery</strong><small>Volgende werkdag voor 18:00</small></span>
                <b>{formatPrice(15)}</b>
              </label>
            </fieldset>
          </section>

          <section>
            <div className="checkout-form__section-title">
              <span>03</span><h2>Payment</h2>
            </div>
            <div className="checkout-payment">
              <div className="checkout-payment__header">
                <span>Credit card</span>
                <div><b>VISA</b><b>MC</b><b>AMEX</b></div>
              </div>
              <div className={fieldClass("cardNumber")}>
                <label htmlFor="cardNumber">Kaartnummer</label>
                <input id="cardNumber" name="cardNumber" inputMode="numeric" value={form.cardNumber} onChange={updateField} autoComplete="cc-number" placeholder="0000 0000 0000 0000" />
                {errors.cardNumber && <p>{errors.cardNumber}</p>}
              </div>
              <div className="checkout-form__grid">
                <div className={fieldClass("expiry")}>
                  <label htmlFor="expiry">Vervaldatum</label>
                  <input id="expiry" name="expiry" inputMode="numeric" value={form.expiry} onChange={updateField} autoComplete="cc-exp" placeholder="MM / JJ" />
                  {errors.expiry && <p>{errors.expiry}</p>}
                </div>
                <div className={fieldClass("cvc")}>
                  <label htmlFor="cvc">CVC</label>
                  <input id="cvc" name="cvc" inputMode="numeric" value={form.cvc} onChange={updateField} autoComplete="cc-csc" placeholder="123" />
                  {errors.cvc && <p>{errors.cvc}</p>}
                </div>
              </div>
              <div className={fieldClass("cardName")}>
                <label htmlFor="cardName">Naam op kaart</label>
                <input id="cardName" name="cardName" value={form.cardName} onChange={updateField} autoComplete="cc-name" />
                {errors.cardName && <p>{errors.cardName}</p>}
              </div>
            </div>

            <label className="checkout-check checkout-check--billing">
              <input type="checkbox" checked={billingSame} onChange={(event) => setBillingSame(event.target.checked)} />
              Factuuradres is hetzelfde als afleveradres.
            </label>

            <button className="checkout-form__submit" type="submit" disabled={submitting}>
              <FiShield aria-hidden="true" />
              {submitting ? "Order wordt verwerkt…" : `Betaal ${formatPrice(total)}`}
            </button>
            <p className="checkout-form__legal">
              Door te betalen ga je akkoord met onze <Link to="/terms">voorwaarden</Link> en <Link to="/privacy">privacyverklaring</Link>.
            </p>
          </section>
        </form>

        <aside className="checkout-summary">
          <p className="eyebrow">Your order / {String(lines.length).padStart(2, "0")}</p>
          <div className="checkout-summary__items">
            {lines.map((item) => (
              <article key={`${item.id}-${item.size}-${item.color}`}>
                <div>
                  <img src={item.product.image} alt={item.product.name} />
                  <span>{item.quantity}</span>
                </div>
                <section>
                  <p>{item.product.category} / {item.product.attributes.fit} fit</p>
                  <h3>{item.product.name}</h3>
                  <small>{item.color} · {item.size}</small>
                </section>
                <strong>{formatPrice(item.product.price * item.quantity)}</strong>
              </article>
            ))}
          </div>
          <dl>
            <div><dt>Subtotaal</dt><dd>{formatPrice(subtotal)}</dd></div>
            <div><dt>Verzending</dt><dd>{shipping === 0 ? "Gratis" : formatPrice(shipping)}</dd></div>
            <div><dt>Totaal</dt><dd>{formatPrice(total)}</dd></div>
          </dl>
          <p className="checkout-summary__note"><FiLock aria-hidden="true" /> Deze demo verwerkt geen echte betaling of kaartgegevens.</p>
        </aside>
      </div>
    </main>
  );
};

export default CheckOut;
